import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { api, type RecurrentStats } from '@/lib/api';
import { useEffect, useState, lazy, Suspense } from 'react';
import { getWebApp } from '@/lib/telegram';
import {
  TrendingUp,
  TrendingDown,
  Users,
  ShoppingBag,
  Wallet,
  Receipt,
  PiggyBank,
  Ticket,
  UserPlus,
  Target,
  BarChart3,
  PieChart as PieIcon,
  ChevronRight,
  Layers,
  RefreshCw,
} from 'lucide-react';
import {
  Skeleton,
  SkeletonCard,
  SkeletonLine,
  SkeletonPage,
  SkeletonStats,
} from '@/components/ui/skeleton';
import { MoneySwitch } from '@/components/SectionSwitch';

const MrrChart = lazy(() => import('@/components/DashboardCharts').then((m) => ({ default: m.MrrChart })));
const TopPlansPie = lazy(() => import('@/components/DashboardCharts').then((m) => ({ default: m.TopPlansPie })));

export const Route = createFileRoute('/stats')({ component: Stats });

type Period = 'today' | 'yesterday' | 'week' | 'month' | '2m' | 'all';

// 'all' → 0 = настоящее «всё время» (бэк трактует period_days=0 как без границы).
const PERIOD_DAYS: Record<Period, number> = {
  today: 1,
  yesterday: 2,
  week: 7,
  month: 30,
  '2m': 60,
  all: 0,
};

const PERIOD_LABELS: Record<Period, string> = {
  today: 'сегодня',
  yesterday: 'за вчера',
  week: 'за неделю',
  month: 'за месяц',
  '2m': 'за 2 месяца',
  all: 'за всё время',
};

const DONUT_COLORS = ['#3390EC', '#FF8A00', '#22C55E', '#EAB308', '#EF4444', '#9F7AEA'];

function Stats() {
  const [period, setPeriod] = useState<Period>('month');
  const [botFilter, setBotFilter] = useState<string | null>(null);

  useEffect(() => {
    const tg = getWebApp();
    tg?.BackButton.hide();
    tg?.MainButton.hide();
  }, []);

  const { data: bots = [] } = useQuery({
    queryKey: ['bots'],
    queryFn: () => api.bots(),
    staleTime: 60 * 1000,
  });

  const recurrentQ = useQuery({
    queryKey: ['partner-recurrent-stats', period, botFilter],
    queryFn: () => api.recurrentStats(PERIOD_DAYS[period], botFilter),
    placeholderData: keepPreviousData,
  });
  const { data: stats, isPending: statsPending } = useQuery({
    queryKey: ['partner-dashboard-stats', period, botFilter],
    queryFn: () => api.dashboardStats(PERIOD_DAYS[period], botFilter),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  if (statsPending && !stats) return <StatsSkeleton />;

  const profit = stats?.margin_rub ?? 0;
  const revenue = stats?.revenue_rub ?? 0;
  const discount = stats?.discount_rub ?? 0;
  const repeatSales = stats?.repeat_sales ?? 0;
  const activeSubs = stats?.active_subs ?? 0;
  const newSubs = stats?.new_subs ?? 0;
  const avgCheck = stats?.avg_check_rub ?? 0;
  const churn = Math.round(stats?.churn_30d_percent ?? 0);
  const newClients = stats?.new_clients ?? 0;
  const convertedClients = stats?.converted_clients ?? 0;
  const conversion = stats?.conversion_percent ?? 0;
  const chartData = stats?.mrr_14d ?? [];
  const topPlans = stats?.top_plans ?? [];
  const breakdown = (stats?.bot_breakdown ?? []).filter((b) => b.revenue_rub > 0);
  const periodLabel = PERIOD_LABELS[period];
  const trendPct = stats?.revenue_change_percent ?? null;
  const hasChart = chartData.length > 0 && chartData.some((d) => d.mrr > 0);
  const showDonut = !botFilter && breakdown.length > 1;

  return (
    <div className="p-4 space-y-4 pb-28">
      <MoneySwitch />
      <div className="px-1 pt-2">
        <h2 className="font-display text-2xl font-bold tracking-tight">Статистика</h2>
      </div>


      {bots.length > 1 && (
        <div className="px-1">
          <label className="eyebrow block mb-1.5">Бот</label>
          <select
            value={botFilter ?? ''}
            onChange={(e) => setBotFilter(e.target.value || null)}
            className="w-full surface-inset rounded-[13px] px-4 py-2.5 text-sm font-semibold focus:outline-none focus:border-white/30 appearance-none cursor-pointer"
          >
            <option value="">Все боты</option>
            {bots.map((b) => (
              <option key={b.id} value={b.id}>
                @{b.username} — {b.brandName}
              </option>
            ))}
          </select>
        </div>
      )}

      <PeriodTabs value={period} onChange={setPeriod} />

      {/* Hero — чистая прибыль за период */}
      <ProfitHero
        profit={profit}
        revenue={revenue}
        discount={discount}
        mirrorBonus={stats?.mirror_bonus_rub ?? 0}
        referralPaid={stats?.referral_paid_rub ?? 0}
        periodLabel={periodLabel}
        trendPct={trendPct}
      />

      {/* Воронка: пришло → купило */}
      <FunnelCard newClients={newClients} converted={convertedClients} conversion={conversion} periodLabel={periodLabel} />

      {/* Вход в UTM-кампании — детализация «откуда пришли и что окупается» */}
      <Link
        to="/campaigns"
        className="surface-card rounded-[18px] p-[14px] flex items-center gap-[11px] active:scale-[0.98] transition-transform"
      >
        <span className="w-9 h-9 rounded-[11px] flex items-center justify-center shrink-0 bg-white/[0.07] text-primary-light">
          <Target className="w-[18px] h-[18px]" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-bold text-[13.5px]">Реклама · UTM-кампании</span>
          <span className="block text-[11px] text-dim">Откуда приходят клиенты и окупается ли закуп</span>
        </span>
        <ChevronRight className="w-4 h-4 text-dim shrink-0" />
      </Link>

      {/* Сетка ключевых метрик */}
      <div className="grid grid-cols-2 gap-[11px]">
        <StatCard accent="primary" icon={<Users />} label="Клиентов сейчас" value={String(activeSubs)} />
        {/* Продления отдельной плиткой, а не в счётчике продаж: приток новых клиентов
            и удержание старых это разные вещи, и одной цифрой они склеиваются. */}
        <StatCard
          accent="violet"
          icon={<ShoppingBag />}
          label={`Продаж ${periodLabel}`}
          value={String(newSubs)}
          hint={repeatSales > 0 ? `+ ${repeatSales} продлений` : undefined}
        />
        <StatCard
          accent="amber"
          icon={<Receipt />}
          label="Средний чек"
          value={avgCheck > 0 ? avgCheck.toLocaleString('ru') : '—'}
          suffix={avgCheck > 0 ? '₽' : undefined}
        />
        <StatCard
          accent="rose"
          icon={<TrendingDown />}
          label="Отвал подписок"
          value={String(churn)}
          suffix={churn > 0 ? '%' : undefined}
        />
      </div>

      <RecurrentCard
        data={recurrentQ.data}
        pending={recurrentQ.isPending && !recurrentQ.data}
        failed={recurrentQ.isError && !recurrentQ.data}
        onRetry={() => recurrentQ.refetch()}
        periodLabel={periodLabel}
      />

      {/* График: оборот + прибыль */}
      <div className="surface-card rounded-[22px] p-[18px]">
        <div className="flex items-center justify-between">
          <span className="eyebrow">Оборот и прибыль · 14 дней</span>
          {trendPct !== null && (
            <span className={`text-[11.5px] font-bold ${trendPct >= 0 ? 'text-money-light' : 'text-danger'}`}>
              {trendPct >= 0 ? '+' : ''}{trendPct}%
            </span>
          )}
        </div>
        {hasChart ? (
          <>
            <div className="h-44 mt-3.5 -mx-2">
              <Suspense fallback={<Skeleton className="w-full h-full rounded-md" />}>
                <MrrChart data={chartData} />
              </Suspense>
            </div>
            <div className="flex items-center justify-center gap-5 mt-1 text-[11px] text-dim">
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-white/80" /> Оборот
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-money-light" /> Прибыль
              </span>
            </div>
          </>
        ) : (
          <div className="h-[120px] flex flex-col items-center justify-center gap-2 text-[#6b6d75]">
            <BarChart3 className="w-[30px] h-[30px]" strokeWidth={1.5} />
            <span className="text-[12.5px]">График появится после первых продаж</span>
          </div>
        )}
      </div>

      {/* Donut: выручка по ботам (только агрегат по всем ботам) */}
      {showDonut && <BotBreakdownCard breakdown={breakdown} />}

      {topPlans.length > 0 && <TopPlansList plans={topPlans} />}
    </div>
  );
}

function ProfitHero({
  profit,
  revenue,
  discount,
  mirrorBonus,
  referralPaid,
  periodLabel,
  trendPct,
}: {
  profit: number;
  revenue: number;
  /** Роздано скидками за период, ₽. Прибыль уже посчитана за их вычетом. */
  discount: number;
  /** Доли из дерева зеркал, ₽: заработок с чужих продаж. */
  mirrorBonus: number;
  /** Реферальные выплаты, ₽. Обычно отрицательные: партнёр платит их из маржи. */
  referralPaid: number;
  periodLabel: string;
  trendPct: number | null;
}) {
  return (
    <div className="rounded-[24px] p-5 surface-card">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <span className="w-[34px] h-[34px] rounded-[11px] bg-[rgba(56,224,165,0.16)] text-money-light flex items-center justify-center">
            <PiggyBank className="w-[18px] h-[18px]" />
          </span>
          <span className="eyebrow text-[#7fdcb8]">Чистая прибыль {periodLabel}</span>
        </div>
        {trendPct !== null && (
          <span
            className={`flex items-center gap-1 text-[12px] font-bold px-2 py-1 rounded-full ${
              trendPct >= 0 ? 'bg-[rgba(56,224,165,0.14)] text-money-light' : 'bg-[rgba(255,90,90,0.12)] text-danger'
            }`}
          >
            {trendPct >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
            {trendPct >= 0 ? '+' : ''}{trendPct}%
          </span>
        )}
      </div>
      {/* Минус красным, как на главной: убыток не цвета денег. */}
      <div className={`font-display font-bold text-[40px] nums leading-none mt-4 ${profit < 0 ? 'text-[#f87171]' : ''}`}>
        {profit.toLocaleString('ru')}
        <span className={`text-2xl ml-1.5 ${profit < 0 ? 'text-[#f87171]' : 'text-money-light'}`}>₽</span>
      </div>
      <div className="flex items-center gap-1.5 text-[12.5px] text-dim mt-2.5">
        <Wallet className="w-[14px] h-[14px]" />
        Оборот: <span className="font-semibold text-foreground/80">{revenue.toLocaleString('ru')} ₽</span>
      </div>
      {/* Скидки по своим ваучерам партнёр оплачивает сам, и они уже вычтены из прибыли.
          Без этой строки уменьшенная цифра читается как недоплата с нашей стороны. */}
      {discount > 0 && (
        <div className="flex items-center gap-1.5 text-[12.5px] text-dim mt-1.5">
          <Ticket className="w-[14px] h-[14px]" />
          Скидки клиентам:{' '}
          <span className="font-semibold text-foreground/80">−{discount.toLocaleString('ru')} ₽</span>
        </div>
      )}
      {/* Доли с зеркал и реферальные выплаты сидят в прибыли, но в продажах партнёра
          их нет: без расшифровки цифра не сходится с тем, что он сам себе посчитал. */}
      {mirrorBonus !== 0 && (
        <div className="flex items-center gap-1.5 text-[12.5px] text-dim mt-1.5">
          <Layers className="w-[14px] h-[14px]" aria-hidden />
          Доли с зеркал:{' '}
          <span className="font-semibold text-foreground/80">
            {mirrorBonus > 0 ? '+' : ''}
            {mirrorBonus.toLocaleString('ru')} ₽
          </span>
        </div>
      )}
      {referralPaid !== 0 && (
        <div className="flex items-center gap-1.5 text-[12.5px] text-dim mt-1.5">
          <Users className="w-[14px] h-[14px]" />
          Рефералы:{' '}
          <span className="font-semibold text-foreground/80">
            {referralPaid > 0 ? '+' : ''}
            {referralPaid.toLocaleString('ru')} ₽
          </span>
        </div>
      )}
    </div>
  );
}

function FunnelCard({
  newClients,
  converted,
  conversion,
  periodLabel,
}: {
  newClients: number;
  converted: number;
  conversion: number;
  periodLabel: string;
}) {
  const fill = Math.min(100, Math.max(0, conversion));
  return (
    <div className="surface-card rounded-[22px] p-[18px] space-y-3.5">
      <div className="flex items-center justify-between">
        <span className="eyebrow">Воронка {periodLabel}</span>
        <span className="font-display font-bold text-[15px] text-primary-light nums">{conversion}%</span>
      </div>
      <div className="grid grid-cols-2 gap-2.5">
        <div className="surface-inset rounded-[15px] p-3">
          <div className="flex items-center gap-1.5 text-dim text-[11px]">
            <UserPlus className="w-[14px] h-[14px]" /> Пришло
          </div>
          <div className="font-display font-bold text-[22px] nums mt-1">{newClients}</div>
        </div>
        <div className="surface-inset rounded-[15px] p-3">
          <div className="flex items-center gap-1.5 text-dim text-[11px]">
            <Target className="w-[14px] h-[14px]" /> Купило
          </div>
          <div className="font-display font-bold text-[22px] nums mt-1 text-money-light">{converted}</div>
        </div>
      </div>
      <div className="w-full h-2.5 bg-white/[0.05] rounded-full overflow-hidden">
        <div
          className="h-full rounded-full bg-white/70 transition-[width] duration-300"
          style={{ width: `${fill}%` }}
        />
      </div>
    </div>
  );
}

function rubMinor(minor: number): string {
  return `${Math.round(minor / 100).toLocaleString('ru')}\u00a0₽`;
}

/**
 * Автосписания у кассы: сколько работает, сколько принесут в месяц и что случилось
 * за период. Те же цифры, что в блоке на сайте (`RecurrentStatsCard`).
 */
function RecurrentCard({
  data,
  pending,
  failed,
  onRetry,
  periodLabel,
}: {
  data: RecurrentStats | undefined;
  pending: boolean;
  failed: boolean;
  onRetry: () => void;
  periodLabel: string;
}) {
  const stopped = data ? data.cancelled + data.ended : 0;
  const cells: Array<{ label: string; value: string; hint?: string; money?: boolean }> = data
    ? [
        {
          label: 'Работают сейчас',
          value: String(data.active),
          hint: data.failing ? `со сбоем ${data.failing}` : data.pending ? `ждут банка ${data.pending}` : undefined,
        },
        { label: 'Принесут в месяц', value: rubMinor(data.monthly_minor), money: true },
        { label: `Подключили ${periodLabel}`, value: String(data.started) },
        {
          label: `Списано ${periodLabel}`,
          value: rubMinor(data.charged_minor),
          hint: `списаний: ${data.charged}`,
          money: true,
        },
        { label: `Не прошло ${periodLabel}`, value: String(data.failed) },
        {
          label: `Отключили ${periodLabel}`,
          value: String(stopped),
          hint: stopped ? `сами: ${data.cancelled}, касса: ${data.ended}` : undefined,
        },
      ]
    : [];
  return (
    <div className="surface-card rounded-[22px] p-[18px] space-y-3">
      <div>
        <div className="flex items-center gap-2">
          <RefreshCw className="w-4 h-4 text-primary-light" />
          <h3 className="font-display font-semibold text-base">Автосписания</h3>
        </div>
        <p className="mt-1 text-[11px] leading-relaxed text-dim">
          Оплатили с галочкой «Продлевать автоматически»: касса сама списывает раз в месяц,
          квартал или год.
        </p>
      </div>
      {pending ? (
        <div className="grid grid-cols-2 gap-2.5">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-[62px] rounded-[15px]" />
          ))}
        </div>
      ) : failed || !data ? (
        <button type="button" onClick={onRetry} className="text-[12.5px] text-dim underline underline-offset-2">
          Не удалось загрузить. Повторить
        </button>
      ) : (
        <div className="grid grid-cols-2 gap-2.5">
          {cells.map((c) => (
            <div key={c.label} className="surface-inset rounded-[15px] p-3 min-w-0">
              <div className="text-[11px] text-dim">{c.label}</div>
              <div className={`font-display font-bold text-[18px] nums mt-1 ${c.money ? 'text-money-light' : ''}`}>
                {c.value}
              </div>
              {c.hint ? <div className="text-[10.5px] text-dim mt-0.5">{c.hint}</div> : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BotBreakdownCard({
  breakdown,
}: {
  breakdown: Array<{ bot_id: string; label: string; revenue_rub: number; sales: number }>;
}) {
  const pieData = breakdown.map((b) => ({ name: b.label, value: b.revenue_rub }));
  const total = breakdown.reduce((s, b) => s + b.revenue_rub, 0);
  return (
    <div className="surface-card rounded-[22px] p-[18px] space-y-3">
      <div className="flex items-center gap-2">
        <PieIcon className="w-4 h-4 text-primary-light" />
        <h3 className="font-display font-semibold text-base">Выручка по ботам</h3>
      </div>
      <div className="h-44 -mx-1">
        <Suspense fallback={<Skeleton className="w-full h-full rounded-md" />}>
          <TopPlansPie data={pieData} />
        </Suspense>
      </div>
      <div className="space-y-1.5">
        {breakdown.map((b, i) => {
          const pct = total > 0 ? Math.round((b.revenue_rub / total) * 100) : 0;
          return (
            <div key={b.bot_id || i} className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2 truncate flex-1 pr-2">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                <span className="truncate">{b.label}</span>
              </span>
              <span className="text-dim text-xs whitespace-nowrap nums">
                {b.revenue_rub.toLocaleString('ru')} ₽ · {pct}%
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TopPlansList({
  plans,
}: {
  plans: Array<{ plan_id: string; plan_name?: string; count: number }>;
}) {
  const total = plans.reduce((sum, p) => sum + p.count, 0);
  return (
    <div className="surface-card rounded-[22px] p-[18px] space-y-3">
      <h3 className="font-display font-semibold text-base">Популярные тарифы</h3>
      <div className="space-y-2.5">
        {plans.map((p, i) => {
          const percent = total > 0 ? Math.round((p.count / total) * 100) : 0;
          const isTop = i === 0;
          return (
            <div key={p.plan_id} className="space-y-1.5">
              <div className="flex justify-between items-baseline text-sm">
                <span className="font-semibold truncate flex-1 pr-2">{p.plan_name || p.plan_id}</span>
                <span className="text-dim text-xs whitespace-nowrap nums">
                  {p.count} продаж · {percent}%
                </span>
              </div>
              <div className="w-full h-2 bg-white/[0.05] rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-[width] duration-300 ${isTop ? 'bg-white/80' : 'bg-white/25'}`}
                  style={{ width: `${percent}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PeriodTabs({ value, onChange }: { value: Period; onChange: (p: Period) => void }) {
  const items: { id: Period; label: string }[] = [
    { id: 'today', label: 'Сегодня' },
    { id: 'yesterday', label: 'Вчера' },
    { id: 'week', label: 'Неделя' },
    { id: 'month', label: 'Месяц' },
    { id: '2m', label: '2 мес' },
    { id: 'all', label: 'Всё' },
  ];
  return (
    <div className="flex gap-[7px] overflow-x-auto overscroll-x-none pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {items.map((it) => (
        <button
          key={it.id}
          type="button"
          onClick={() => onChange(it.id)}
          className={`shrink-0 px-4 py-2 rounded-full text-[12.5px] font-bold whitespace-nowrap transition-colors duration-100 ${
            value === it.id ? 'bg-white/[0.12] text-foreground' : 'bg-white/[0.05] text-muted-foreground'
          }`}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  suffix,
  hint,
  accent,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  suffix?: string;
  /** Вторая строка под цифрой: уточнение, которое не тянет на отдельную плитку. */
  hint?: string;
  accent: 'primary' | 'violet' | 'money' | 'amber' | 'rose';
}) {
  const cfg = {
    primary: { tile: 'surface-card', icon: 'bg-white/[0.07] text-primary-light', label: '', suffix: 'text-primary-light' },
    violet: { tile: 'surface-card', icon: 'bg-[rgba(159,122,234,0.16)] text-violet', label: '', suffix: 'text-violet' },
    money: {
      tile: 'surface-card',
      icon: 'bg-[rgba(56,224,165,0.14)] text-money-light',
      label: 'text-[#7fdcb8]',
      suffix: 'text-money-light',
    },
    amber: { tile: 'surface-card', icon: 'bg-[rgba(245,166,35,0.15)] text-amber', label: '', suffix: 'text-amber' },
    rose: { tile: 'surface-card', icon: 'bg-[rgba(255,90,90,0.13)] text-danger', label: '', suffix: 'text-danger' },
  }[accent];
  return (
    <div className={`rounded-[20px] p-4 min-h-[112px] flex flex-col ${cfg.tile}`}>
      <span className={`w-[34px] h-[34px] rounded-[11px] flex items-center justify-center [&_svg]:w-[17px] [&_svg]:h-[17px] ${cfg.icon}`}>
        {icon}
      </span>
      <div className={`eyebrow mt-auto ${cfg.label}`}>{label}</div>
      <div className="font-display font-bold text-[26px] nums mt-1.5 leading-none">
        {value}
        {suffix && <span className={`text-base ml-1 ${cfg.suffix}`}>{suffix}</span>}
      </div>
      {hint && <div className="text-[11.5px] text-dim mt-1 leading-none">{hint}</div>}
    </div>
  );
}

/**
 * Заголовок «Статистика» настоящий, приветствие с именем — плейсхолдер.
 * Дальше: переключатель периода, карточка прибыли, воронка, четыре плитки цифр.
 */
function StatsSkeleton() {
  return (
    <SkeletonPage className="space-y-4 pb-28">
      {/* Место под переключатель «Кошелёк / Выводы / … / Статистика». */}
      <Skeleton className="h-[38px] w-[330px] max-w-full rounded-full" />
      <div className="px-1 pt-2">
        <h2 className="font-display text-2xl font-bold tracking-tight">Статистика</h2>
      </div>
      <Skeleton className="h-10 rounded-full" />
      <SkeletonCard className="rounded-[24px] min-h-0 p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Skeleton className="w-[34px] h-[34px] rounded-[11px] shrink-0" />
          <SkeletonLine size="xs" className="w-40" />
        </div>
        <SkeletonLine size="hero" className="w-2/5" />
        <SkeletonLine size="sm" className="w-1/3" />
      </SkeletonCard>
      <SkeletonCard className="rounded-[22px] min-h-0 p-5 space-y-3">
        <SkeletonLine size="xs" className="w-32" />
        <div className="flex items-center gap-3">
          <SkeletonLine size="hero" className="w-16" />
          <Skeleton className="h-2 flex-1 rounded-full" />
          <SkeletonLine size="hero" className="w-16" />
        </div>
        <SkeletonLine size="sm" className="w-1/2" />
      </SkeletonCard>
      <SkeletonStats count={4} cols={2} className="gap-[11px]" itemClassName="rounded-[20px] p-[15px]" />
      <SkeletonCard className="h-56 rounded-[22px]" />
    </SkeletonPage>
  );
}
